import { api } from '@organization/api'
import { PASSWORD_PATTERN } from '@organization/app-constants'
import { showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import { App, Button, Flex, Form, Input } from 'antd'
import { useTranslation } from 'react-i18next'

type Props = {
  passwordToken?: string
  onCancel: () => void
}

export const ChangePasswordForm = ({ passwordToken, onCancel }: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { message } = App.useApp()

  const { mutate, isPending } = useMutation({
    mutationFn: api.account.changePasswordConfirm,

    onSuccess: () => {
      message.success(t('password_changed'))
      onCancel?.()
    },

    onError: (e) => {
      showApiErrors(e, form)
    }
  })

  const onSubmit = async (values: {
    password: string
    confirm_password: string
  }) => {
    if (!passwordToken) return

    mutate({
      ...values,
      token: passwordToken
    })
  }

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={onSubmit}
    >
      <Form.Item
        label={t('form.item.new_password')}
        name="password"
        rules={[
          {
            pattern: PASSWORD_PATTERN,
            message: t('form.message.new_password_regex')
          },
          { required: true, message: t('form.message.password_required') }
        ]}
      >
        <Input.Password placeholder={t('form.placeholder.new_password')} />
      </Form.Item>

      <Form.Item
        label={t('form.item.confirm_new_password')}
        name="confirm_password"
        dependencies={['password']}
        rules={[
          {
            pattern: PASSWORD_PATTERN,
            message: t('form.message.new_password_regex')
          },
          {
            required: true,
            message: t('form.message.confirm_new_password')
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve()
              }
              return Promise.reject(new Error(t('error.password_no_match')))
            }
          })
        ]}
      >
        <Input.Password
          placeholder={t('form.placeholder.confirm_new_password')}
        />
      </Form.Item>

      <Flex justify="end" gap={16}>
        <Button type="default" onClick={onCancel}>
          {t('button.cancel')}
        </Button>

        <Button type="primary" loading={isPending} htmlType="submit">
          {t('button.submit')}
        </Button>
      </Flex>
    </Form>
  )
}
